import { IconFromSet } from '@gettactic/components';
import { Armchair, DoorClosed } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import {
  IconShape,
  ZoneLink,
  ZoneShape,
  ZoneTypeDefinition
} from '../../types';

export interface LegendPointOfInterest {
  category: ZoneTypeDefinition;
  /** How many categorized icons of this category exist on the map. */
  count: number;
  /** Representative icon (first found) — its glyph illustrates the row. */
  sample: IconShape;
}

export interface ZoneLegendProps {
  zoneTypes: ZoneTypeDefinition[];
  /** Categories actually referenced by zones on this map. Categories only
   *  used by icons show under Points of Interest instead. */
  zoneCategoryIds?: Set<string>;
  /** Categorized icons grouped per category, with counts. */
  pointsOfInterest?: LegendPointOfInterest[];
  /** Hover sync for points of interest: matching icons get a halo. */
  setIconCategoryHover?: React.Dispatch<React.SetStateAction<string | null>>;
  visibleZoneCategories: Set<string>;
  setVisibleZoneCategories: React.Dispatch<React.SetStateAction<Set<string>>>;
  toggleZoneCategory: (id: string) => void;
  setZoneCategoryHover: React.Dispatch<React.SetStateAction<string | null>>;
  /** Zones with a navigation link — rendered as a clickable list. */
  linkedZones?: ZoneShape[];
  /** Navigates when a linked-zone entry is clicked. Entries only render
   *  when this is provided. */
  onLinkedZoneClick?: (link: ZoneLink) => void;
  /** Hover sync with the map: hovering an entry brightens the zone. */
  setHoveredLinkZoneId?: React.Dispatch<React.SetStateAction<string | null>>;
  /** Per-category total area in square meters. `null`/undefined when the
   *  map isn't calibrated (no pixelsPerMeter set), or the viewer isn't an
   *  admin — areas are admin-only and hidden for everyone else. */
  zoneAreasByCategory?: Map<string, number> | null;
  /** Desk and meeting-room counts per zone category. Shown for every
   *  viewer (unlike area, not admin-gated). */
  zoneResourceCountsByCategory?: Map<
    string,
    { desks: number; meetingRooms: number }
  > | null;
  /** Distance from the bottom of the viewport. Defaults to clearing the
   *  minimap + zoom controls the web host renders alongside this (bottom:
   *  20 + 150 height, then zoom controls to bottom: 180 + ~50 tall — see
   *  below). Hosts with no such widgets (e.g. the mobile app's WebView, no
   *  minimap/zoom controls of its own) should pass a smaller value so the
   *  legend doesn't float with a wide empty gap beneath it. Accepts a CSS
   *  string (e.g. a `calc()` with `env(safe-area-inset-bottom)`) as well
   *  as a plain pixel number. */
  bottomOffset?: number | string;
}

const sectionLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 600,
  color: '#9ca3af',
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  margin: '6px 0 2px'
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '5px 6px',
  borderRadius: 6,
  border: 'none',
  background: 'transparent',
  color: '#1f2937',
  fontSize: 13,
  width: '100%',
  textAlign: 'left'
};

/** Stacked-layers glyph for the legend pill/header. */
const LayersIcon: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#111827"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    <path d="M12 2 2 7l10 5 10-5-10-5Z" />
    <path d="m2 12 10 5 10-5" />
    <path d="m2 17 10 5 10-5" />
  </svg>
);

const ChevronIcon: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
  <svg
    width={16}
    height={16}
    viewBox="0 0 24 24"
    fill="none"
    stroke="#6b7280"
    strokeWidth={2.5}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
    style={{
      transform: isOpen ? 'rotate(180deg)' : undefined,
      transition: 'transform 150ms ease'
    }}
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

/** Compact icon + count, used for desk/meeting-room tallies where a text
 *  label ("3 desks") wouldn't fit the legend's fixed width. */
const CountBadge: React.FC<{
  icon: React.ReactNode;
  count: number;
  title: string;
}> = ({ icon, count, title }) => (
  <span
    title={title}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 2,
      fontSize: 10,
      color: '#6b7280',
      fontVariantNumeric: 'tabular-nums'
    }}
  >
    {icon}
    {count}
  </span>
);

/** Representative glyph for a points-of-interest row. */
const PoiGlyph: React.FC<{ icon: IconShape }> = ({ icon }) =>
  icon.iconUrl ? (
    <img
      src={icon.iconUrl}
      alt=""
      style={{ width: 16, height: 16, objectFit: 'contain', opacity: 0.8 }}
    />
  ) : (
    <IconFromSet
      icon={icon.icon}
      color="#4b5563"
      style={{ width: 16, height: 16 }}
    />
  );

/**
 * Floating legend overlay for the map: points of interest (categorized
 * icons, with counts), zone categories, and linked locations. Collapsed by
 * default — the header toggles it open.
 *
 * Portaled to `document.body` so it sits outside the host app's pan/zoom
 * container (which would otherwise consume the pointer events) and stays
 * pinned to the viewport above the minimap + zoom controls.
 */
export const ZoneLegend: React.FC<ZoneLegendProps> = ({
  zoneTypes,
  zoneCategoryIds,
  pointsOfInterest = [],
  setIconCategoryHover,
  visibleZoneCategories,
  setVisibleZoneCategories,
  toggleZoneCategory,
  setZoneCategoryHover,
  linkedZones = [],
  onLinkedZoneClick,
  setHoveredLinkZoneId,
  zoneAreasByCategory,
  zoneResourceCountsByCategory,
  bottomOffset = 240
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  // Grand totals across all categories, shown at the bottom of the Zones
  // section.
  const totalAreaSqm = React.useMemo(() => {
    if (!zoneAreasByCategory) return null;
    let sum = 0;
    for (const value of zoneAreasByCategory.values()) sum += value;
    return sum;
  }, [zoneAreasByCategory]);

  const totalResourceCounts = React.useMemo(() => {
    if (!zoneResourceCountsByCategory) return null;
    let desks = 0;
    let meetingRooms = 0;
    for (const value of zoneResourceCountsByCategory.values()) {
      desks += value.desks;
      meetingRooms += value.meetingRooms;
    }
    return { desks, meetingRooms };
  }, [zoneResourceCountsByCategory]);

  // Linked zones only earn legend space when the host can navigate them.
  const actionableLinkedZones = onLinkedZoneClick ? linkedZones : [];
  // Zone rows: categories actually painted by zones on this map. Icon-only
  // categories live in the Points of Interest section instead.
  const zoneCategories = zoneCategoryIds
    ? zoneTypes.filter((t) => zoneCategoryIds.has(t.id))
    : zoneTypes;

  if (typeof document === 'undefined') return null;
  if (
    zoneCategories.length === 0 &&
    pointsOfInterest.length === 0 &&
    actionableLinkedZones.length === 0
  ) {
    return null;
  }

  return createPortal(
    <div
      // Belt-and-suspenders: also stop propagation on pointer events
      // in case some host wires a listener at document or window level.
      onMouseDown={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onPointerDown={(e) => {
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
      }}
      onWheel={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        // Stack above the host's existing bottom-right widgets (default
        // 240 clears a minimap at bottom: 20 + 150 height and zoom
        // controls at bottom: 180, ~50 tall) — see `bottomOffset` doc.
        bottom: bottomOffset,
        right: 20,
        display: 'flex',
        flexDirection: 'column',
        width: isOpen ? 232 : undefined,
        padding: isOpen ? '10px 12px 12px' : '10px 18px',
        background: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid rgba(0, 0, 0, 0.08)',
        // Collapsed: a fully-rounded pill (icon + "Legend").
        borderRadius: isOpen ? 12 : 9999,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
        fontFamily: 'Quicksand, sans-serif',
        userSelect: 'none',
        zIndex: 50,
        pointerEvents: 'auto'
      }}
    >
      <button
        type="button"
        onClick={() => setIsOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: isOpen ? 'space-between' : 'center',
          gap: 10,
          border: 'none',
          background: 'transparent',
          padding: isOpen ? '0 0 6px' : 0,
          cursor: 'pointer',
          fontSize: 'var(--text-button, 13.5px)',
          // Quiet pill when collapsed; bold section title when open.
          fontWeight: isOpen ? 700 : 400,
          color: '#111827',
          fontFamily: 'inherit'
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {!isOpen && <LayersIcon />}
          <span>Legend</span>
        </span>
        {isOpen && <ChevronIcon isOpen={isOpen} />}
      </button>

      {isOpen && (
        <>
          {pointsOfInterest.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Points of Interest</div>
              {pointsOfInterest.map(({ category, count, sample }) => (
                <div
                  key={category.id}
                  onMouseEnter={() => setIconCategoryHover?.(category.id)}
                  onMouseLeave={() => setIconCategoryHover?.(null)}
                  style={{ ...rowStyle, cursor: 'default' }}
                >
                  <PoiGlyph icon={sample} />
                  <span style={{ flex: 1 }}>{category.name}</span>
                  <span
                    style={{
                      minWidth: 20,
                      height: 20,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: 10,
                      background: '#f3f4f6',
                      color: '#4b5563',
                      fontSize: 11,
                      fontWeight: 600,
                      padding: '0 6px'
                    }}
                  >
                    {count}
                  </span>
                </div>
              ))}
            </>
          )}

          {zoneCategories.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Zones</div>
              {zoneCategories.map((t) => {
                const isVisible = visibleZoneCategories.has(t.id);
                const areaSqm = zoneAreasByCategory?.get(t.id);
                const resourceCounts = zoneResourceCountsByCategory?.get(t.id);
                return (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => toggleZoneCategory(t.id)}
                    onMouseEnter={() => setZoneCategoryHover(t.id)}
                    onMouseLeave={() => setZoneCategoryHover(null)}
                    title={isVisible ? 'Click to hide' : 'Click to show'}
                    style={{
                      ...rowStyle,
                      cursor: 'pointer',
                      color: isVisible ? '#1f2937' : '#9ca3af',
                      fontFamily: 'inherit'
                    }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 4,
                        background: isVisible ? `${t.color}55` : 'transparent',
                        border: `2px solid ${t.color}`,
                        opacity: isVisible ? 1 : 0.45
                      }}
                    />
                    <span
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {t.name}
                    </span>
                    {!!resourceCounts?.desks && (
                      <CountBadge
                        icon={<Armchair size={11} />}
                        count={resourceCounts.desks}
                        title="Desks"
                      />
                    )}
                    {!!resourceCounts?.meetingRooms && (
                      <CountBadge
                        icon={<DoorClosed size={11} />}
                        count={resourceCounts.meetingRooms}
                        title="Meeting rooms"
                      />
                    )}
                    {areaSqm !== undefined && (
                      <span
                        style={{
                          fontSize: 10,
                          color: '#9ca3af',
                          fontVariantNumeric: 'tabular-nums'
                        }}
                      >
                        {areaSqm.toFixed(1)} m²
                      </span>
                    )}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() =>
                  setVisibleZoneCategories(
                    visibleZoneCategories.size > 0
                      ? new Set()
                      : new Set(zoneCategories.map((t) => t.id))
                  )
                }
                style={{
                  fontSize: 11,
                  color: '#2563eb',
                  background: 'transparent',
                  border: 'none',
                  padding: '2px 6px 0',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: 'inherit'
                }}
              >
                {visibleZoneCategories.size > 0 ? 'Hide all' : 'Show all'}
              </button>
              {((totalAreaSqm !== null && totalAreaSqm > 0) ||
                !!totalResourceCounts?.desks ||
                !!totalResourceCounts?.meetingRooms) && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    marginTop: 4,
                    paddingTop: 4,
                    borderTop: '1px solid rgba(0, 0, 0, 0.08)',
                    fontSize: 11,
                    fontWeight: 600,
                    color: '#374151'
                  }}
                >
                  <span>Total</span>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {!!totalResourceCounts?.desks && (
                      <CountBadge
                        icon={<Armchair size={12} />}
                        count={totalResourceCounts.desks}
                        title="Desks"
                      />
                    )}
                    {!!totalResourceCounts?.meetingRooms && (
                      <CountBadge
                        icon={<DoorClosed size={12} />}
                        count={totalResourceCounts.meetingRooms}
                        title="Meeting rooms"
                      />
                    )}
                    {totalAreaSqm !== null && totalAreaSqm > 0 && (
                      <span>{totalAreaSqm.toFixed(1)} m²</span>
                    )}
                  </span>
                </div>
              )}
            </>
          )}

          {actionableLinkedZones.length > 0 && (
            <>
              <div style={sectionLabelStyle}>Locations</div>
              {actionableLinkedZones.map((zone) => {
                const link = zone.link;
                if (!link) return null;
                const label = zone.name || link.label || link.url || 'Open map';
                return (
                  <button
                    type="button"
                    key={zone.id}
                    onClick={() => onLinkedZoneClick?.(link)}
                    onMouseEnter={() => setHoveredLinkZoneId?.(zone.id)}
                    onMouseLeave={() => setHoveredLinkZoneId?.(null)}
                    title={link.label || link.url || label}
                    style={{ ...rowStyle, cursor: 'pointer', fontFamily: 'inherit' }}
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        width: 14,
                        height: 14,
                        borderRadius: 4,
                        background: zone.color || '#93c5fd',
                        border: '1px solid rgba(0, 0, 0, 0.12)'
                      }}
                    />
                    <span style={{ flex: 1 }}>{label}</span>
                    <span aria-hidden style={{ color: '#9ca3af' }}>
                      ↗
                    </span>
                  </button>
                );
              })}
            </>
          )}

          <div
            style={{
              marginTop: 8,
              fontSize: 12,
              color: '#9ca3af'
            }}
          >
            Hover to locate on the map
          </div>
        </>
      )}
    </div>,
    document.body
  );
};
